/**
 * Media Viewer Service
 *
 * MIME-type detection shared by the attachment pieces and the carousel footer.
 * The source normalization, URL and error helpers that lived beside these went
 * with the media viewer modal in GitLab #1539 (nothing else called them).
 */

import { MediaType } from '../types/media';

/**
 * MIME type mappings for media type detection
 */
const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/heic',
  'image/heif',
];

const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
  'video/ogg',
  'video/mpeg',
];

/**
 * Video formats with broad browser support
 * MP4 with H.264 codec has the best cross-browser compatibility
 */
const WEB_SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg'];

const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/aac',
  'audio/x-m4a',
];

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/**
 * Detect media type from MIME type
 *
 * @param mimeType - MIME type string (e.g., 'image/jpeg')
 * @returns MediaType category
 */
export const detectMediaType = (mimeType?: string): MediaType => {
  if (!mimeType) {
    return 'unknown';
  }

  const normalizedMimeType = mimeType.toLowerCase();

  if (isImageMimeType(normalizedMimeType)) {
    return 'image';
  }

  if (isVideoMimeType(normalizedMimeType)) {
    return 'video';
  }

  if (isAudioMimeType(normalizedMimeType)) {
    return 'audio';
  }

  if (isDocumentMimeType(normalizedMimeType)) {
    return 'document';
  }

  return 'unknown';
};

/**
 * Check if MIME type is an image
 *
 * @param mimeType - MIME type to check
 * @returns true if image type
 */
export const isImageMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalized = mimeType.toLowerCase();
  return (
    IMAGE_MIME_TYPES.includes(normalized) || normalized.startsWith('image/')
  );
};

/**
 * Check if MIME type is a video
 *
 * @param mimeType - MIME type to check
 * @returns true if video type
 */
export const isVideoMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalized = mimeType.toLowerCase();
  return (
    VIDEO_MIME_TYPES.includes(normalized) || normalized.startsWith('video/')
  );
};

/**
 * Check if MIME type is audio
 *
 * @param mimeType - MIME type to check
 * @returns true if audio type
 */
export const isAudioMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalized = mimeType.toLowerCase();
  return (
    AUDIO_MIME_TYPES.includes(normalized) || normalized.startsWith('audio/')
  );
};

/**
 * Check if MIME type is a document
 *
 * @param mimeType - MIME type to check
 * @returns true if document type
 */
export const isDocumentMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalized = mimeType.toLowerCase();
  return DOCUMENT_MIME_TYPES.includes(normalized);
};
